(() => {
  if (window.__orskaProductGridReady) return;
  window.__orskaProductGridReady = true;

  const rootSelector = 'results-list.orska-product-grid-results';
  const groupSelector = '.filter-group, .sfd-section';
  const panelSelector = '.filter-group-panel, .sfd-section-panel';
  const headSelector = '.filter-group-head, .sfd-section-head';
  const stickyOffset = 90;
  let sectionController;

  const getRoot = (target) => {
    if (target instanceof Element) return target.closest(rootSelector);
    return document.querySelector(rootSelector);
  };

  const getDrawerParts = (root) => ({
    drawer: root?.querySelector('#shop-filter-drawer'),
    backdrop: root?.querySelector('#shop-filter-backdrop'),
    trigger: root?.querySelector('#sfd-trigger'),
  });

  const getPanel = (group) => group.querySelector(panelSelector);

  const normalizeFilterGroups = (scope = document) => {
    scope.querySelectorAll('[data-orska-filter-groups]').forEach((container) => {
      const mode = container.dataset.filterMode || 'desktop';
      const selector = mode === 'drawer' ? '.sfd-section' : '.filter-group';

      Array.from(container.children)
        .filter((child) => child.matches?.(selector))
        .forEach((group, index) => {
          const hasChecked = !!group.querySelector('input:checked');
          const shouldExpand = index === 0 || hasChecked;
          const button = group.querySelector('button');
          group.classList.toggle('expanded', shouldExpand);
          button?.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
        });
    });
  };

  const cloneDesktopGroupForDrawer = (root, group, groupIndex) => {
    const clone = group.cloneNode(true);
    clone.classList.remove('filter-group');
    clone.classList.add('sfd-section');

    clone.querySelector('.filter-group-head')?.classList.replace('filter-group-head', 'sfd-section-head');
    clone.querySelector('.filter-group-chev')?.classList.replace('filter-group-chev', 'sfd-chev');
    clone.querySelector('.filter-group-panel')?.classList.replace('filter-group-panel', 'sfd-section-panel');
    clone.querySelector('.filter-group-options')?.classList.replace('filter-group-options', 'sfd-options');
    clone.querySelectorAll('.filter-option-label').forEach((label) => label.classList.replace('filter-option-label', 'sfd-option-label'));
    clone.querySelectorAll('.box').forEach((box) => box.classList.replace('box', 'sfd-box'));
    clone.querySelectorAll('.filter-label').forEach((label) => label.classList.replace('filter-label', 'sfd-label'));
    clone.querySelectorAll('.count').forEach((count) => count.classList.replace('count', 'sfd-count'));

    clone.querySelectorAll('input.filter-input').forEach((input, inputIndex) => {
      const oldId = input.id;
      const nextId = `Drawer-Filter-${root?.getAttribute('section-id') || 'section'}-${groupIndex + 1}-${inputIndex + 1}`;
      input.classList.replace('filter-input', 'sfd-input');
      input.setAttribute('form', 'shop-filter-drawer-form');
      input.id = nextId;

      const label = Array.from(clone.querySelectorAll('label')).find((item) => item.getAttribute('for') === oldId);
      label?.setAttribute('for', nextId);
    });

    return clone;
  };

  const syncDrawerFilters = (root) => {
    if (!root) return;

    const drawerBody = root.querySelector('.sfd-body');
    const desktopGroups = Array.from(root.querySelectorAll('.filters .filter-group'));
    if (!drawerBody || !desktopGroups.length) return;

    drawerBody.replaceChildren(...desktopGroups.map((group, index) => cloneDesktopGroupForDrawer(root, group, index)));
  };

  const prepareFilters = (root) => {
    if (!root) return;
    normalizeFilterGroups(root);
    syncDrawerFilters(root);
  };

  const setPanelHeight = (panel, height) => {
    if (!panel) return;
    panel.style.maxHeight = typeof height === 'number' ? `${height}px` : height;
  };

  const syncFilterPanels = (scope = document) => {
    scope.querySelectorAll(groupSelector).forEach((group) => {
      const panel = getPanel(group);
      if (!panel) return;

      if (group.classList.contains('expanded')) {
        panel.style.maxHeight = 'none';
        setPanelHeight(panel, panel.scrollHeight);
      } else {
        setPanelHeight(panel, 0);
      }
    });
  };

  const openFilterPanel = (group) => {
    const panel = getPanel(group);
    const head = group.querySelector(headSelector);
    if (!panel) return;

    group.classList.add('expanded');
    head?.setAttribute('aria-expanded', 'true');
    setPanelHeight(panel, 0);
    requestAnimationFrame(() => {
      setPanelHeight(panel, panel.scrollHeight);
    });
  };

  const closeFilterPanel = (group) => {
    const panel = getPanel(group);
    const head = group.querySelector(headSelector);
    if (!panel) return;

    setPanelHeight(panel, panel.scrollHeight);
    group.classList.remove('expanded');
    head?.setAttribute('aria-expanded', 'false');
    requestAnimationFrame(() => {
      setPanelHeight(panel, 0);
    });
  };

  const toggleFilterPanel = (group) => {
    if (group.classList.contains('expanded')) closeFilterPanel(group);
    else openFilterPanel(group);
  };

  const scrollProductGridIntoView = (root) => {
    if (!root) return;

    const top = root.getBoundingClientRect().top + window.scrollY - stickyOffset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  };

  const openDrawer = (root) => {
    prepareFilters(root);
    const { drawer, backdrop, trigger } = getDrawerParts(root);
    if (!drawer || !trigger) return;
    // Cloned desktop groups carry an inline max-height:0px (desktop filters are
    // display:none on mobile, so their measured scrollHeight was 0). Unclip the
    // panels that are meant to start expanded so the first group is visible.
    drawer.querySelectorAll('.sfd-section.expanded .sfd-section-panel').forEach((panel) => {
      panel.style.maxHeight = 'none';
    });
    drawer.classList.add('open');
    backdrop?.classList.add('open');
    document.body.classList.add('nav-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
  };

  const closeDrawer = (root) => {
    const { drawer, backdrop, trigger } = getDrawerParts(root);
    drawer?.classList.remove('open');
    backdrop?.classList.remove('open');
    document.body.classList.remove('nav-drawer-open');
    drawer?.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
  };

  const buildFormUrl = (form) => {
    const url = new URL(form.action || window.location.href, window.location.origin);
    url.search = '';

    for (const [key, value] of new FormData(form).entries()) {
      if (value !== '') url.searchParams.append(key, value);
    }

    url.searchParams.delete('page');
    return url;
  };

  const fetchSection = async (root, url, pushState = true) => {
    const sectionId = root?.getAttribute('section-id');
    const sectionWrapper = root?.closest('.shopify-section');
    if (!root || !sectionId || !sectionWrapper) return;

    sectionController?.abort();
    sectionController = new AbortController();

    const browserUrl = new URL(url.toString(), window.location.origin);
    browserUrl.searchParams.delete('section_id');

    const fetchUrl = new URL(browserUrl.toString());
    fetchUrl.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
    fetchUrl.searchParams.sort();

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(fetchUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: sectionController.signal,
      });

      if (!response.ok) throw new Error(response.status);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextWrapper = parsed.getElementById(sectionWrapper.id);
      const nextRoot = nextWrapper?.querySelector(rootSelector);

      if (!nextWrapper || !nextRoot) {
        window.location.href = browserUrl.toString();
        return;
      }

      closeDrawer(root);
      sectionWrapper.replaceWith(nextWrapper);

      if (pushState) {
        window.history.pushState({ orskaProductGrid: true }, '', browserUrl.toString());
      }

      const activeRoot = document.querySelector(rootSelector);
      prepareFilters(activeRoot);
      syncFilterPanels(activeRoot ?? document);
      scrollProductGridIntoView(activeRoot);
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = browserUrl.toString();
    } finally {
      const activeRoot = document.querySelector(rootSelector);
      activeRoot?.classList.remove('is-loading');
      activeRoot?.removeAttribute('aria-busy');
    }
  };

  const syncCheckedState = (input) => {
    input.closest('li')?.classList.toggle('checked', input.checked);
  };

  const scrollToHash = () => {
    const url = new URL(window.location.href);
    if (!url.hash) return;

    const root = document.querySelector(rootSelector);
    const target = document.getElementById(url.hash.slice(1));
    if (!root || !target || !root.contains(target)) return;

    scrollProductGridIntoView(root);
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const root = getRoot(target);
    if (!root) return;

    if (target.closest('#sfd-trigger')) {
      event.preventDefault();
      openDrawer(root);
      return;
    }

    if (target.closest('#sfd-close') || target.closest('#shop-filter-backdrop')) {
      event.preventDefault();
      closeDrawer(root);
      return;
    }

    const collapsibleHead = target.closest(headSelector);
    if (collapsibleHead) {
      event.preventDefault();
      const group = collapsibleHead.closest(groupSelector);
      if (!group) return;
      toggleFilterPanel(group);
      return;
    }

    const clearLink = target.closest('.filters-head .clear, .sfd-clear');
    if (clearLink) {
      event.preventDefault();
      fetchSection(root, new URL(clearLink.href, window.location.origin));
      return;
    }

    const paginationLink = target.closest('.shop-pagination a[href]');
    if (paginationLink) {
      event.preventDefault();
      fetchSection(root, new URL(paginationLink.href, window.location.origin));
      scrollProductGridIntoView(document.querySelector(rootSelector));
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const root = getRoot(target);
    if (!root) return;

    if (target.matches('.sfd-input')) {
      syncCheckedState(target);
      return;
    }

    if (target.matches('.filter-input')) {
      syncCheckedState(target);
      const form = target.closest('form');
      if (form) fetchSection(root, buildFormUrl(form));
      return;
    }

    if (target.matches('.sort select, .sfd-trigger-sort select')) {
      const form = target.closest('form');
      if (form) fetchSection(root, buildFormUrl(form));
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const root = getRoot(form);
    if (!root) return;

    if (form.matches('.filters-form, .sort, .sfd-trigger-sort, .sfd-form')) {
      event.preventDefault();
      fetchSection(root, buildFormUrl(form));
    }
  });

  document.addEventListener(
    'transitionend',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.propertyName !== 'max-height') return;

      const panel = event.target.closest(panelSelector);
      const group = panel?.closest(groupSelector);
      if (!panel || !group?.classList.contains('expanded')) return;

      panel.style.maxHeight = 'none';
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(rootSelector).forEach((root) => closeDrawer(root));
  });

  window.addEventListener('popstate', () => {
    const root = document.querySelector(rootSelector);
    if (root) fetchSection(root, new URL(window.location.href), false);
  });

  window.addEventListener('resize', () => {
    const root = document.querySelector(rootSelector);
    prepareFilters(root);
    syncFilterPanels(root ?? document);
  });

  const init = () => {
    const root = document.querySelector(rootSelector);
    prepareFilters(root);
    syncFilterPanels(root ?? document);
    scrollToHash();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
